import type {
  Coordinate,
  HotelConstraintQL,
  HotelConstraintSelectionQL,
  ItinerarySearchCriteria,
  URLString,
} from '@libs/trip-planner-interfaces';
import {
  InputBoardBasisQL,
  SelectedHotelTypeQL,
  AccommodationType,
  BoardBasisFilter,
} from '@libs/trip-planner-interfaces';
import {
  allElementsAreDefined,
  assertValue,
  exhaustiveGuard,
  generateRandomUUID,
  isDefined,
  getDomain,
  localeStringComparer,
} from '@libs/utils';
import type { HotelConstraint, HotelSelection } from '@libs/constraint-builder';
import {
  deserializeConstraint,
  DestinationConstraint,
  FullTextConstraint,
  HotelCodeConstraint,
  isDestinationConstraint,
  isFullTextConstraint,
  isHotelCodeConstraint,
  isPolygonConstraint,
  isPolygonIdConstraint,
  isThemeConstraint,
  PolygonConstraint,
  PolygonIdConstraint,
  serializeConstraint,
  ThemeConstraint,
} from '@libs/constraint-builder';

export function isSameStringContent(stringA: string, stringB: string): boolean {
  return (
    localeStringComparer(stringA, stringB, {
      sensitivity: 'accent',
      ignorePunctuation: true,
    }) === 0
  );
}

export const LOCAL_SESSION_ID = generateRandomUUID();

export function setSessionId(criteria: ItinerarySearchCriteria): void {
  const sessionId = criteria.userCriteria.sessionId;
  if (!sessionId) {
    criteria.userCriteria.sessionId = LOCAL_SESSION_ID;
  }
}

const DEFAULT_MARKET_ORIGIN: URLString = 'https://www.sembo.co.uk';

export function getMarketOrigin(options?: {
  forceDefaultMarket?: boolean;
}): URLString {
  const marketOrigin = options?.forceDefaultMarket
    ? DEFAULT_MARKET_ORIGIN
    : getDomain.isProduction
    ? window.location.origin
    : DEFAULT_MARKET_ORIGIN;

  const WHITELABEL_SUBDOMAIN = 'whitelabel';
  const GENERIC_SUBDOMAIN = 'www';
  const urlString = marketOrigin.replace(
    WHITELABEL_SUBDOMAIN,
    GENERIC_SUBDOMAIN
  );

  if (!isValidURLString(urlString)) {
    throw new Error(`Invalid market origin: ${marketOrigin}`);
  }

  return urlString;
}

function isValidURLString(urlString: string): urlString is URLString {
  try {
    const url = new URL(urlString);
    return !!url.protocol && !!url.host;
  } catch (error) {
    return false;
  }
}

export function getDomainMarketOrigin(options?: {
  forceDefaultMarket?: boolean;
}): string {
  const market = getMarketOrigin(options);
  const endSchema = '://';
  const pathIndex = market.indexOf(endSchema) + endSchema.length;
  return market.slice(pathIndex);
}

export function getHotelConstraint(
  constraintQL: HotelConstraintQL
): HotelConstraint & { selectedHotelTypes: AccommodationType[] } & {
  boardBasis: BoardBasisFilter[];
} {
  let selection: HotelSelection | undefined;

  if (constraintQL.destination) {
    selection = new FullTextConstraint(constraintQL.destination, true);
  }
  if (constraintQL.polygon && allElementsAreDefined(constraintQL.polygon)) {
    const coordinates: Coordinate[] = constraintQL.polygon.map(
      (coordinateQL) => ({
        latitude: coordinateQL.latitude,
        longitude: coordinateQL.longitude,
      })
    );
    selection = new PolygonConstraint(coordinates);
  }
  if (
    constraintQL.hotelCodes &&
    allElementsAreDefined(constraintQL.hotelCodes)
  ) {
    selection = new HotelCodeConstraint(...constraintQL.hotelCodes);
  }
  if (constraintQL.tagId) {
    selection = new ThemeConstraint(constraintQL.tagId);
  }
  if (constraintQL.polygonId) {
    selection = new PolygonIdConstraint(constraintQL.polygonId);
  }
  if (constraintQL.destinationCode) {
    selection = new DestinationConstraint(constraintQL.destinationCode);
  }
  if (constraintQL.serializedConstraint) {
    const { selection: constraintSelection } = deserializeConstraint(
      constraintQL.serializedConstraint
    );
    selection = constraintSelection;
  }

  if (!selection) {
    throw new Error('No valid selection provided');
  }

  const {
    maxNumberOfHotels,
    hasParking,
    hasPool,
    hasSpa,
    hasGym,
    hasInternet,
    hasAirConditioning,
    hasElectricVehicleChargingStation,
    petsAreAllowed,
    isBusinessProperty,
    isFamilyProperty,
    starRating,
    freeCancellation,
    boardBasis,
    selectedHotelTypes,
  } = constraintQL;

  return {
    selection,
    maxNumberOfHotels: maxNumberOfHotels ?? undefined,
    starRating: starRating ?? undefined,
    onlyHotelsWithParking: hasParking ?? undefined,
    onlyHotelsWithPool: hasPool ?? undefined,
    onlyHotelsWithSpa: hasSpa ?? undefined,
    onlyHotelsWithGym: hasGym ?? undefined,
    onlyHotelsWithWifi: hasInternet ?? undefined,
    onlyHotelsWithAir: hasAirConditioning ?? undefined,
    onlyHotelsWithVehicleChargingStation:
      hasElectricVehicleChargingStation ?? undefined,
    onlyHotelsWithPetsAllowed: petsAreAllowed ?? undefined,
    onlyHotelsWithBusinessProperty: isBusinessProperty ?? undefined,
    onlyHotelsWithFamilyProperty: isFamilyProperty ?? undefined,
    onlyHotelsWithFreeCancellation: freeCancellation ?? undefined,
    boardBasis: (boardBasis ?? []).filter(isDefined).map(toBoardBasisFilter),
    selectedHotelTypes: (selectedHotelTypes ?? [])
      .filter(isDefined)
      .map(toAccommodationType),
  };
}

export function getHotelConstraintSelectionQL(
  constraint: HotelConstraint
): HotelConstraintSelectionQL {
  const { selection } = constraint;

  if (isFullTextConstraint(selection)) {
    return { destination: selection.freeText };
  }

  if (isPolygonConstraint(selection)) {
    return { polygon: selection.coordinates };
  }

  if (isHotelCodeConstraint(selection)) {
    return {
      hotelCodes: selection.hotelCodes,
    };
  }

  if (isPolygonIdConstraint(selection)) {
    return {
      polygonId: Number(selection.serializeValue()),
    };
  }

  if (isThemeConstraint(selection)) {
    return {
      tagId: Number(selection.serializeValue()),
    };
  }

  if (isDestinationConstraint(selection)) {
    return {
      destinationCode: Number(selection.serializeValue()),
    };
  }

  return {
    serializedConstraint: serializeConstraint(constraint),
  };
}

export function getHotelConstraintQL(
  constraint: HotelConstraint & {
    selectedHotelTypes: AccommodationType[];
    selectedBoardBasis: BoardBasisFilter[];
  }
): HotelConstraintQL {
  const {
    maxNumberOfHotels,
    onlyHotelsWithParking,
    onlyHotelsWithPetsAllowed,
    onlyHotelsWithBusinessProperty,
    onlyHotelsWithFamilyProperty,
    onlyHotelsWithFreeCancellation,
    onlyHotelsWithPool,
    onlyHotelsWithSpa,
    onlyHotelsWithGym,
    onlyHotelsWithWifi,
    onlyHotelsWithAir,
    onlyHotelsWithVehicleChargingStation,
    starRating,
    selectedHotelTypes,
    selectedBoardBasis,
    hotelName,
  } = constraint;

  return {
    ...getHotelConstraintSelectionQL(constraint),
    selectedHotelTypes: selectedHotelTypes.map(toSelectedHotelType),
    maxNumberOfHotels,
    starRating,
    hasParking: onlyHotelsWithParking,
    hasPool: onlyHotelsWithPool,
    hasSpa: onlyHotelsWithSpa,
    hasGym: onlyHotelsWithGym,
    hasInternet: onlyHotelsWithWifi,
    hasElectricVehicleChargingStation: onlyHotelsWithVehicleChargingStation,
    hasAirConditioning: onlyHotelsWithAir,
    petsAreAllowed: onlyHotelsWithPetsAllowed,
    isBusinessProperty: onlyHotelsWithBusinessProperty,
    isFamilyProperty: onlyHotelsWithFamilyProperty,
    freeCancellation: onlyHotelsWithFreeCancellation,
    boardBasis: selectedBoardBasis?.map(toSelectedInputBoardBasisQL),
    getBoards: false,
    hotelName,
  };
}

function toAccommodationType(
  selectedHotelTypeQL: SelectedHotelTypeQL
): AccommodationType {
  switch (selectedHotelTypeQL) {
    case SelectedHotelTypeQL.Apartment:
      return AccommodationType.Apartment;

    case SelectedHotelTypeQL.HouseOrCottage:
      return AccommodationType.HouseOrCottage;

    case SelectedHotelTypeQL.Hotel:
      return AccommodationType.Hotel;

    case SelectedHotelTypeQL.Ticket:
      return AccommodationType.Ticket;

    case SelectedHotelTypeQL.Golf:
      return AccommodationType.Golf;

    case SelectedHotelTypeQL.None:
      throw new Error('None is not a supported AccommodationType');

    default:
      exhaustiveGuard(
        selectedHotelTypeQL,
        'Unhandled InputSelectedHotelTypesQL'
      );
  }
}

export function toSelectedHotelType(
  accommodationType: AccommodationType
): SelectedHotelTypeQL {
  switch (accommodationType) {
    case AccommodationType.Hotel:
      return SelectedHotelTypeQL.Hotel;

    case AccommodationType.HouseOrCottage:
      return SelectedHotelTypeQL.HouseOrCottage;

    case AccommodationType.Apartment:
      return SelectedHotelTypeQL.Apartment;

    case AccommodationType.Ticket:
      return SelectedHotelTypeQL.Ticket;

    case AccommodationType.Golf:
      return SelectedHotelTypeQL.Golf;

    default:
      exhaustiveGuard(accommodationType, 'Unhandled AccommodationType');
  }
}

export function toSelectedInputBoardBasisQL(
  boardBasis: BoardBasisFilter
): InputBoardBasisQL {
  switch (boardBasis) {
    case BoardBasisFilter.AllInclusive:
      return InputBoardBasisQL.AllInclusive;

    case BoardBasisFilter.Breakfast:
      return InputBoardBasisQL.Breakfast;

    case BoardBasisFilter.FullBoard:
      return InputBoardBasisQL.FullBoard;

    case BoardBasisFilter.HalfBoard:
      return InputBoardBasisQL.HalfBoard;

    case BoardBasisFilter.RoomOnly:
      return InputBoardBasisQL.RoomOnly;

    default:
      exhaustiveGuard(boardBasis, 'Unhandled BoardBasisFilter');
  }
}
export function toBoardBasisFilter(
  boardBasisQL: InputBoardBasisQL
): BoardBasisFilter {
  switch (boardBasisQL) {
    case InputBoardBasisQL.AllInclusive:
      return BoardBasisFilter.AllInclusive;

    case InputBoardBasisQL.Breakfast:
      return BoardBasisFilter.Breakfast;

    case InputBoardBasisQL.FullBoard:
      return BoardBasisFilter.FullBoard;

    case InputBoardBasisQL.HalfBoard:
      return BoardBasisFilter.HalfBoard;

    case InputBoardBasisQL.RoomOnly:
      return BoardBasisFilter.RoomOnly;

    case InputBoardBasisQL.Lunch:
      throw new Error('Lunch is not a supported BoardBasisFilter');

    default:
      exhaustiveGuard(boardBasisQL, 'Unhandled InputBoardBasisQL');
  }
}

export function mapEnumValue<T, U>(
  enum1: Record<string, T>,
  enum2: Record<string, U>,
  value: T,
  predicate: (iteratee: T) => boolean = (enum1Value): boolean =>
    enum1Value === value
): U {
  const enumKey = Object.keys(enum1)[Object.values(enum1).findIndex(predicate)];
  assertValue(
    enumKey,
    `search-service-smart-search > utils.ts > mapEnumValue > enumKey > ${value} > ${Object.values(
      enum1
    ).join(', ')}`
  );

  const enumValue = enum2[enumKey];
  assertValue(
    enumValue,
    'search-service-smart-search > utils.ts > mapEnumValue > enumValue'
  );

  return enumValue;
}

// TODO: Remove SmartSearch assertions when Insights confirm their Schema consistency
export function assertSmartSearchList<T>(
  possibleList: (T | null | undefined)[] | null | undefined
): asserts possibleList is T[] {
  if (!isDefined(possibleList) || !allElementsAreDefined(possibleList)) {
    throw new Error('Not valid SmartSearch list output');
  }
}

export function assertSmartSearchValue<T>(
  possibleValue: T | null | undefined
): asserts possibleValue is T {
  if (!isDefined(possibleValue)) {
    throw new Error('Not valid SmartSearch value output');
  }
}
